/**
 * LegalTranslationHub — landing page for the Legal Translation feature.
 *
 * Layout:
 *   • Header + "New Translation" primary button
 *   • Stats strip (total / this month / by language)
 *   • Filters (source lang, target lang, search)
 *   • List of past translations with: id, langs, doc type, preview, actions
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, RefreshCw, Trash2, FileDown, Eye, Languages, Search, FileText, Image as ImageIcon,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LegalTranslationHub() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [srcFilter, setSrcFilter] = useState('all');
  const [tgtFilter, setTgtFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, langs] = await Promise.all([
        axios.get(`${API_URL}/api/admin/legal-translation`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/legal-translation/languages`, { headers: getAuthHeaders() }),
      ]);
      setRows(r.data.data || []);
      setLanguages(langs.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const removeRow = async (id) => {
    if (!window.confirm(t('حذف هذه الترجمة؟', 'Delete this translation?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/legal-translation/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const downloadPdf = (id) => {
    const token = getAuthHeaders().Authorization?.replace(/^Bearer /, '') || '';
    const url = `${API_URL}/api/admin/legal-translation/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const filtered = rows.filter(r => {
    if (srcFilter !== 'all' && r.source_lang !== srcFilter) return false;
    if (tgtFilter !== 'all' && r.target_lang !== tgtFilter) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      return (
        (r.id || '').toLowerCase().includes(needle)
        || (r.doc_type || '').toLowerCase().includes(needle)
        || (r.file_name || '').toLowerCase().includes(needle)
        || (r.source_text || '').toLowerCase().includes(needle)
        || (r.translated_text || '').toLowerCase().includes(needle)
      );
    }
    return true;
  });

  const stats = {
    total:    rows.length,
    doc:      rows.filter(r => r.mode === 'document').length,
    txt:      rows.filter(r => r.mode === 'text').length,
    thisWeek: rows.filter(r => r.created_at && (Date.now() - new Date(r.created_at).getTime()) < 7 * 24 * 3600 * 1000).length,
  };

  const langLabel = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? (isAr ? lang.ar : lang.en) : code;
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#10b981]/6 rounded-full blur-[150px]" />
      </div>

      {/* Feb 2026: moved into منصة العملاء ▸ طلبات الخدمة */}
      <DashboardHeader showBackButton backPath="/manage/cloudgm?tab=services-categories" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/manage/cloudgm' },
            { label_ar: 'طلبات الخدمة', label_en: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
            { label_ar: 'الترجمة القانونية (AI)', label_en: 'Legal Translation (AI)', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-2 mb-2">
              <Languages className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 text-sm font-medium tracking-wide">
                {t('CLOUDGM FZ LLC — الترجمة القانونية', 'CLOUDGM FZ LLC — Legal Translation')}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('الترجمة القانونية', 'Legal Translation')}
            </h1>
            <p className="text-white/50 text-sm max-w-2xl">
              {t('ترجمة نصوص ومستندات قانونية (جوازات، شهادات، رخص، عقود) بالذكاء الاصطناعي Gemini — يحافظ على التنسيق والأسماء والأرقام كما هي في الأصل.',
                 'Translate legal texts and scanned documents (passports, certificates, licences, contracts) with Gemini AI — preserves layout, names, and identifiers.')}
            </p>
          </div>
          <Button
            onClick={() => navigate('/dashboard/cloudgm/legal-translation/new')}
            className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-bold h-11 px-5"
            data-testid="lt-new-btn"
          >
            <Plus className="w-4 h-4 me-2" />
            {t('ترجمة جديدة', 'New Translation')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label={t('المجموع', 'Total')} value={stats.total} color="#10b981" />
          <StatCard label={t('مستندات', 'Documents')} value={stats.doc} color="#0ea5e9" />
          <StatCard label={t('نصوص', 'Texts')} value={stats.txt} color="#f59e0b" />
          <StatCard label={t('هذا الأسبوع', 'This week')} value={stats.thisWeek} color="#d4af37" />
        </div>

        {/* Filters */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-white/50 text-xs mb-1.5">{t('البحث', 'Search')}</label>
              <div className="relative">
                <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder={t('معرّف، نوع مستند، أو نص...', 'ID, doc type, or text...')}
                       className="bg-[#050505] border-white/10 text-white ps-9"
                       data-testid="lt-search" />
              </div>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5">{t('من', 'From')}</label>
              <select value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)}
                      dir={isAr ? 'rtl' : 'ltr'}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm">
                <option value="all">{t('الكل', 'All')}</option>
                {languages.map(l => <option key={l.code} value={l.code}>{isAr ? l.ar : l.en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1.5">{t('إلى', 'To')}</label>
              <select value={tgtFilter} onChange={(e) => setTgtFilter(e.target.value)}
                      dir={isAr ? 'rtl' : 'ltr'}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm">
                <option value="all">{t('الكل', 'All')}</option>
                {languages.map(l => <option key={l.code} value={l.code}>{isAr ? l.ar : l.en}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={load} variant="outline"
                      className="w-full border-white/10 bg-transparent text-white/80 hover:bg-white/5">
                <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
                {t('تحديث', 'Refresh')}
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-sm">
              {t('لا توجد ترجمات بعد. اضغط "ترجمة جديدة" لبدء الأولى.',
                 'No translations yet. Click "New Translation" to create the first one.')}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map(row => (
                <Row key={row.id}
                     row={row}
                     langLabel={langLabel}
                     onView={() => navigate(`/dashboard/cloudgm/legal-translation/${row.id}`)}
                     onPdf={() => downloadPdf(row.id)}
                     onDelete={() => removeRow(row.id)}
                     t={t} isAr={isAr} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


function StatCard({ label, value, color }) {
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-xs text-white/50">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}


function Row({ row, langLabel, onView, onPdf, onDelete, t, isAr }) {
  const isDoc = row.mode === 'document';
  const Icon = isDoc ? (row.file_mime_type?.startsWith('image/') ? ImageIcon : FileText) : Languages;
  const preview = (row.translated_text || row.source_text || '').slice(0, 140);
  return (
    <div className="p-4 hover:bg-white/[0.03] transition-colors">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
          <Icon className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
              {row.id}
            </span>
            <span className="text-white font-semibold text-sm">
              {langLabel(row.source_lang)} → {langLabel(row.target_lang)}
            </span>
            {row.doc_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
                {row.doc_type}
              </span>
            )}
          </div>
          {preview && (
            <div className="text-white/60 text-xs line-clamp-2" dir="auto">{preview}</div>
          )}
          <div className="text-white/40 text-[11px] mt-1">
            {new Date(row.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB', {
              year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
            })}
            {row.duration_ms && <span className="ms-2">· {(row.duration_ms / 1000).toFixed(1)}s</span>}
            {row.model && <span className="ms-2 opacity-60">· {row.model}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onView}
                  className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white"
                  title={t('عرض', 'View')}
                  data-testid={`lt-view-${row.id}`}>
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={onPdf}
                  className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white"
                  title={t('تحميل PDF', 'Download PDF')}
                  data-testid={`lt-pdf-${row.id}`}>
            <FileDown className="w-4 h-4" />
          </button>
          <button onClick={onDelete}
                  className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                  title={t('حذف', 'Delete')}
                  data-testid={`lt-delete-${row.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
